import React, { useMemo, useState, useCallback } from 'react';

import BasicCRUD from './../core/basic-crud';
import FadeAlert from "../core/fade-alert";
import PopUpDialog from "../core/popup-dialog";
import { useAuth } from "../../AuthContext";
import { AUTH_URL, PRODUCT_ID } from "../../const";

// ------- i18n -------
const dict = {
    en: {
        // page
        title: "WorkSpaces",
        subTitle: "add workspace",
        homeURL: "/dashboard",
        addTitle: "Create Workspace",
        editTitle: "Edit Workspace",

        // columns
        colName: "Name",
        colRegDate: "Registration Date",
        colExpDate: "Expiration Date",
        colStatus: "Status",
        colPricingPlan: "Pricing Plan",
        colUsersCount: "Users Count",

        // form
        tabMain: "Main",
        companyName: "Company Name",
        noteLine1:
            "Choose a clear workspace name – it will be shown in menus and dashboards for every user.",
        noteLine2:
            "Use your company or project name so team members can quickly identify the workspace.",

        // actions
        delete: "Delete",
        edit: "Edit",
        switchWorkspace: "Switch",
        currentWorkspaceLabel: "Current workspace",
        currentWorkspaceBadge: "Active",

        switchSuccess: "Workspace switched successfully.",
        switchError: "Failed to switch workspace. Try again.",
        transferOwnership: "Transfer ownership",
        transferDialogTitle: 'Transfer ownership: "{name}"',
        transferDescription: "Select a member to become the new owner. This action cannot be undone.",
        transferSelectLabel: "Select new owner",
        transferSelectPlaceholder: "-- choose member --",
        transferConfirmLabel: 'Type "{name}" to confirm transfer',
        transferSubmit: "Transfer ownership",
        transferLoadError: "Unable to load members. Switch to this workspace and try again.",
        transferError: "Unable to transfer ownership. Try again.",
        transferSuccess: "Ownership transferred successfully.",
        transferLoading: "Loading members…",
        transferEmpty: "No eligible members found.",

        // delete dialog
        delAlert: "Deleting a workspace cannot be undone.",
        delText: "To confirm deletion, enter the name of the workspace in the next input field",
        delTitle: 'Delete workspace: "{name}"',

        // toasts
        toastEntity: "Workspace",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
    },

    ru: {
        title: "Рабочие пространства",
        subTitle: "добавление рабочего пространства",
        homeURL: "/dashboard",
        addTitle: "Создать рабочее пространство",
        editTitle: "Редактировать рабочее пространство",

        colName: "Название",
        colRegDate: "Дата регистрации",
        colExpDate: "Дата окончания",
        colStatus: "Статус",
        colPricingPlan: "Тарифный план",
        colUsersCount: "Количество пользователей",

        tabMain: "Основное",
        companyName: "Название компании",
        noteLine1:
            "Укажите понятное название рабочей области — его увидят все участники в меню и списках.",
        noteLine2:
            "Лучше всего использовать название компании или проекта, чтобы коллеги легко находили нужный workspace.",

        edit: "Редактировать",
        delete: "Удалить",
        switchWorkspace: "Переключить",
        currentWorkspaceLabel: "Текущая рабочая область",
        currentWorkspaceBadge: "Активная",

        switchSuccess: "Рабочая область переключена.",
        switchError: "Не удалось переключить рабочую область. Попробуйте снова.",
        transferOwnership: "Передать права владельца",
        transferDialogTitle: 'Передача прав владельца: «{name}»',
        transferDescription: "Выберите участника, который станет новым владельцем. Действие необратимо.",
        transferSelectLabel: "Новый владелец",
        transferSelectPlaceholder: "-- выберите пользователя --",
        transferConfirmLabel: 'Введите «{name}» для подтверждения',
        transferSubmit: "Передать владение",
        transferLoadError: "Не удалось загрузить участников. Переключитесь на этот workspace и попробуйте снова.",
        transferError: "Не удалось передать права. Попробуйте снова.",
        transferSuccess: "Права владельца успешно переданы.",
        transferLoading: "Загрузка списка участников…",
        transferEmpty: "Нет доступных кандидатов.",

        delAlert: "Удаление рабочего пространства необратимо.",
        delText: "Чтобы подтвердить удаление, введите название рабочего пространства в следующее поле ввода",
        delTitle: 'Удалить рабочее пространство: «{name}»',

        toastEntity: "Рабочее пространство",
        createdOk: "успешно создано",
        updatedOk: "успешно обновлено",
        deletedOk: "успешно удалено",
    },

    ua: {
        title: "Робочі простори",
        subTitle: "додавання робочого простору",
        homeURL: "/dashboard",
        addTitle: "Створити робочий простір",
        editTitle: "Редагувати робочий простір",

        colName: "Назва",
        colRegDate: "Дата реєстрації",
        colExpDate: "Дата завершення",
        colStatus: "Статус",
        colPricingPlan: "Тарифний план",
        colUsersCount: "Кількість користувачів",

        tabMain: "Основне",
        companyName: "Назва компанії",
        noteLine1:
            "Вкажіть зрозумілу назву робочого простору — її бачитимуть усі учасники в меню та списках.",
        noteLine2:
            "Найкраще використовувати назву компанії або проекту, щоб колеги легко впізнавали workspace.",

        edit: "Редагувати",
        delete: "Видалити",
        switchWorkspace: "Перемкнути",
        currentWorkspaceLabel: "Поточний робочий простір",
        currentWorkspaceBadge: "Активний",

        switchSuccess: "Робочий простір перемкнено.",
        switchError: "Не вдалося перемкнути робочий простір. Спробуйте ще раз.",
        transferOwnership: "Передати права власника",
        transferDialogTitle: 'Передача прав власника: «{name}»',
        transferDescription: "Оберіть учасника, який стане новим власником. Дію не можна скасувати.",
        transferSelectLabel: "Новий власник",
        transferSelectPlaceholder: "-- оберіть користувача --",
        transferConfirmLabel: 'Введіть «{name}» для підтвердження',
        transferSubmit: "Передати власність",
        transferLoadError: "Не вдалося завантажити учасників. Перемкніться на цей workspace і спробуйте ще раз.",
        transferError: "Не вдалося передати права. Спробуйте ще раз.",
        transferSuccess: "Права власника успішно передано.",
        transferLoading: "Завантаження учасників…",
        transferEmpty: "Немає доступних кандидатів.",

        delAlert: "Видалення робочого простору неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву робочого простору у наступне поле вводу",
        delTitle: 'Видалити робочий простір: «{name}»',

        toastEntity: "Робочий простір",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ------- component -------
const Workspaces = ({ lang: langProp, ...props }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const { user, selectWorkspace } = useAuth();
    const currentUserId = user?.user_id || user?.id;
    const currentWorkspaceId = user?.company_id || user?.workspace_id || user?.current_workspace_id;

    const getWorkspaceId = (workspace) =>
        workspace?.company_id ?? workspace?.workspace_id ?? workspace?.id;

    const isCurrentWorkspace = (workspace) => {
        const targetId = getWorkspaceId(workspace);
        if (!targetId || !currentWorkspaceId) return false;
        return String(targetId) === String(currentWorkspaceId);
    };

    const fetchWorkspaceMembers = useCallback(async () => {
        if (!user?.token) {
            setTransferError(t.transferLoadError);
            return;
        }
        setTransferMembersLoading(true);
        setTransferError("");
        try {
            const response = await fetch(`${AUTH_URL}company/current/users`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: user.token,
                },
                body: JSON.stringify({
                    product_id: PRODUCT_ID,
                    include_inactive: "1",
                    page: 1,
                    per_page: 250,
                    q: "",
                }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload?.message || t.transferLoadError);
            }
            const items = Array.isArray(payload?.items) ? payload.items : [];
            setTransferMembers(items);
        } catch (error) {
            setTransferError(error?.message || t.transferLoadError);
            setTransferMembers([]);
        } finally {
            setTransferMembersLoading(false);
        }
    }, [user?.token, t.transferLoadError]);

    const openTransferDialog = useCallback((workspace) => {
        setTransferWorkspace(workspace);
        setSelectedNewOwner("");
        setTransferConfirmInput("");
        setTransferError("");
        setTransferMembers([]);
        setShowTransferDialog(true);
        fetchWorkspaceMembers();
    }, [fetchWorkspaceMembers]);

    const closeTransferDialog = useCallback(() => {
        setShowTransferDialog(false);
        setTransferWorkspace(null);
        setTransferMembers([]);
        setSelectedNewOwner("");
        setTransferConfirmInput("");
        setTransferError("");
    }, []);

    const object = "workspaces";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [statusAlert, setStatusAlert] = useState({ type: "", text: "" });
    const [refreshKey, setRefreshKey] = useState(0);
    const [showTransferDialog, setShowTransferDialog] = useState(false);
    const [transferWorkspace, setTransferWorkspace] = useState(null);
    const [transferMembers, setTransferMembers] = useState([]);
    const [transferMembersLoading, setTransferMembersLoading] = useState(false);
    const [transferSubmitting, setTransferSubmitting] = useState(false);
    const [transferError, setTransferError] = useState("");
    const [selectedNewOwner, setSelectedNewOwner] = useState("");
    const [transferConfirmInput, setTransferConfirmInput] = useState("");

    const PricingPlan = (data) => {
        const styles = {
            container: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
            plan_name: { fontWeight: 'bold', fontSize: 16, marginBottom: 4 },
            expiration_date: { fontSize: 12, color: '#6c757d' },
        };

        return <div style={styles.container}>
            <span style={styles.plan_name}>{data['plan_name']}</span>
            {data.expiration_date && (
                <span style={styles.expiration_date}>{data['expiration_date']}</span>
            )}
        </div>
    };

    const formatWorkspaceName = (workspace) => {
        const isActive = isCurrentWorkspace(workspace);
        return (
            <div className="d-flex align-items-center justify-content-between gap-2">
                <div>
                    <div className="fw-semibold">{workspace.name || "-"}</div>
                    {isActive && (
                        <div className="text-muted small">
                            {t.currentWorkspaceLabel || "Current workspace"}
                        </div>
                    )}
                </div>
                {isActive && (
                    <span className="badge bg-primary text-uppercase">
                        {t.currentWorkspaceBadge || "Active"}
                    </span>
                )}
            </div>
        );
    };

    const columns = [
        { label: t.colName, key: "name", format: formatWorkspaceName },
        { label: t.colPricingPlan, key: "plan_name", format: PricingPlan },
        { label: t.colUsersCount, key: "users_count" },
        { label: t.colStatus, key: "user_role" },
    ];

    const fields = {
        add: [
            {
                type: "tab",
                name: t.tabMain,
                image: "",
                fields: [
                    [
                        {
                            label: "",
                            type: "custom",
                            fn: () => (
                                <>
                                    {t.noteLine1}<br />
                                    {t.noteLine2}
                                </>
                            ),
                        },
                    ],
                    [
                        { label: t.companyName, key: "name", default_value: "", showLabel: "top" },
                    ],
                ],
            },
        ],
        edit: [
            {
                type: "tab",
                name: t.tabMain,
                image: "",
                fields: [
                    [
                        {
                            label: "",
                            type: "custom",
                            fn: () => (
                                <>
                                    {t.noteLine1}<br />
                                    {t.noteLine2}
                                </>
                            ),
                        },
                    ],
                    [
                        { label: t.companyName, key: "name", default_value: "", showLabel: "top" },
                    ],
                ],
            },
        ],
    };

    const canDeleteWorkspace = (workspace) => {
        if (!workspace) return false;
        const workspaceRole = (workspace.user_role || workspace.role || "").toLowerCase();
        const isOwner = workspaceRole === "owner";
        const hasSingleUser = Number(workspace.users_count) <= 1;
        const isWorkspaceOwner = workspace.owner_id
            ? String(workspace.owner_id) === String(currentUserId)
            : true;
        return isOwner && hasSingleUser && isWorkspaceOwner;
    };

    const hasOwnerAccess = (workspace) => {
        if (!workspace) return false;
        const workspaceRole = (workspace.user_role || workspace.role || "").toLowerCase();
        return workspaceRole === "owner";
    };

    const handleTransferSubmit = useCallback(async () => {
        if (!transferWorkspace || !selectedNewOwner || !user?.token) return;
        const workspaceId = getWorkspaceId(transferWorkspace);
        if (!workspaceId) {
            setTransferError(t.transferError);
            return;
        }
        setTransferSubmitting(true);
        setTransferError("");
        try {
            const response = await fetch(`${AUTH_URL}transfer-ownership`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: user.token,
                },
                body: JSON.stringify({
                    company_id: workspaceId,
                    new_owner_id: Number(selectedNewOwner),
                }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload?.message || t.transferError);
            }
            setStatusAlert({ type: "success", text: t.transferSuccess });
            closeTransferDialog();
            setRefreshKey((prev) => prev + 1);
        } catch (error) {
            setTransferError(error?.message || t.transferError);
        } finally {
            setTransferSubmitting(false);
        }
    }, [transferWorkspace, selectedNewOwner, user?.token, t.transferError, t.transferSuccess, closeTransferDialog]);

    const actions = [
        {
            name: "switch",
            label: t.switchWorkspace,
            controller: "custom",
            icon: "ti ti-arrows-exchange",
            fn: (_name, row) => !isCurrentWorkspace(row),
        },
        {
            name: "transfer",
            label: t.transferOwnership,
            controller: "custom",
            icon: "ti ti-user-up",
            fn: (_name, row) => hasOwnerAccess(row) && isCurrentWorkspace(row),
        },
        {
            name: "edit",
            label: t.edit,
            controller: "crud",
            icon: "ti ti-edit",
            fn: (_name, row) => hasOwnerAccess(row),
        },
        {
            name: "delete",
            label: t.delete,
            controller: "crud",
            icon: "ti ti-trash",
            fn: (_name, row) => canDeleteWorkspace(row),
        },
    ];

    const handleWorkspaceAction = useCallback(async (action, data) => {
        if (action === "switch") {
            const workspaceId = data?.company_id || data?.workspace_id || data?.id;
            if (!workspaceId) {
                setStatusAlert({ type: "error", text: t.switchError });
                return;
            }
            try {
                await selectWorkspace(workspaceId);
                setStatusAlert({ type: "success", text: t.switchSuccess });
            } catch (error) {
                setStatusAlert({
                    type: "error",
                    text: error?.message || t.switchError,
                });
            }
            return;
        }
        if (action === "transfer") {
            openTransferDialog(data);
        }
    }, [selectWorkspace, t.switchError, t.switchSuccess, openTransferDialog]);

    const confirmTarget = transferWorkspace?.name?.trim() || "";
    const availableMembers = useMemo(() => {
        return (transferMembers || [])
            .filter((member) => String(member.id) !== String(currentUserId))
            .map((member) => {
                const fullName = `${member.first_name || ""} ${member.last_name || ""}`.trim();
                const label = fullName || member.email || `ID ${member.id}`;
                const role = member.user_role || member.user_type || "";
                return {
                    id: member.id,
                    label,
                    email: member.email,
                    role: role ? role.toLowerCase() : "",
                };
            });
    }, [transferMembers, currentUserId]);
    const canSubmitTransfer = Boolean(selectedNewOwner) && transferConfirmInput.trim() === confirmTarget;
    const isTransferDisabled = transferSubmitting || !canSubmitTransfer;

    const renderWorkspaceCard = (item, helpers) => {
        const isActive = isCurrentWorkspace(item);
        const planBlock = PricingPlan(item);
        const actionsList = (helpers.actions || []).filter(
            (action) => !action.fn || action.fn(action.name, item)
        );
        const handleAction = (actionName) => helpers.tableAction?.(actionName, item);

        return (
            <div className={`card h-100 shadow-sm border ${isActive ? "border-primary" : ""}`}>
                <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                            <h5 className="mb-0">{item.name}</h5>
                            {isActive && (
                                <div className="text-muted small">{t.currentWorkspaceLabel || "Current workspace"}</div>
                            )}
                        </div>
                        {isActive && (
                            <span className="badge bg-primary">{t.currentWorkspaceBadge || t.switchWorkspace}</span>
                        )}
                    </div>
                    <div className="text-muted small mb-2">
                        {t.colStatus}: <span className="fw-semibold text-dark">{item.user_role || t.colStatus}</span>
                    </div>
                    <div className="text-muted small mb-2">
                        {t.colUsersCount}: <span className="fw-semibold text-dark">{item.users_count ?? 0}</span>
                    </div>
                    <div className="mb-4">{planBlock}</div>

                    <div className="mt-auto d-flex gap-2 justify-content-end flex-wrap">
                        {actionsList.map((action) => (
                            <button
                                key={action.name}
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                title={action.label}
                                onClick={() => handleAction(action.name)}
                            >
                                <i className={action.icon || "ti ti-dots"}></i>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <BasicCRUD
                key={`workspaces-${refreshKey}`}
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addTitle}
                editTitle={t.editTitle}
                object={object}
                columns={columns}
                fields={fields}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                tableAction={handleWorkspaceAction}
                deleteConfirmationDialog={{
                    alert: t.delAlert,
                    text: t.delText,
                    title: t.delTitle,
                    column: 'name',
                }}
                viewModes={["table", "blocks"]}
                renderItem={renderWorkspaceCard}
                cardLayout={{ columnClass: "col-12 col-lg-6 col-xxl-4" }}
            >
                {statusAlert.text && (
                    <div className={`alert alert-${statusAlert.type === "error" ? "danger" : "success"} mb-3`}>
                        {statusAlert.text}
                    </div>
                )}

                {response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                        {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                        {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>

            <PopUpDialog
                title={(t.transferDialogTitle || "").replace("{name}", transferWorkspace?.name || "")}
                show={showTransferDialog}
                hideDialog={closeTransferDialog}
                footer={
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleTransferSubmit}
                            disabled={isTransferDisabled}
                        >
                            {transferSubmitting ? `${t.transferSubmit}…` : t.transferSubmit}
                        </button>
                        <button
                            type="button"
                            className="btn bg-danger-subtle text-danger waves-effect"
                            onClick={closeTransferDialog}
                        >
                            Close
                        </button>
                    </div>
                }
            >
                {transferError && (
                    <div className="alert alert-danger mb-3">{transferError}</div>
                )}
                <p className="mb-3">{t.transferDescription}</p>
                <div className="mb-3">
                    <label className="form-label fw-semibold">{t.transferSelectLabel}</label>
                    <select
                        className="form-select"
                        value={selectedNewOwner}
                        onChange={(event) => setSelectedNewOwner(event.target.value)}
                        disabled={transferMembersLoading || !availableMembers.length}
                    >
                        <option value="">{t.transferSelectPlaceholder}</option>
                        {availableMembers.map((member) => (
                            <option key={member.id} value={member.id}>
                                {member.label}
                                {member.email ? ` (${member.email})` : ""}
                            </option>
                        ))}
                    </select>
                    {transferMembersLoading && (
                        <div className="text-muted small mt-1">{t.transferLoading}</div>
                    )}
                    {!transferMembersLoading && !availableMembers.length && (
                        <div className="text-muted small mt-1">{t.transferEmpty}</div>
                    )}
                </div>
                <div className="mb-0">
                    <label className="form-label fw-semibold">
                        {(t.transferConfirmLabel || "").replace("{name}", transferWorkspace?.name || "")}
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        value={transferConfirmInput}
                        onChange={(event) => setTransferConfirmInput(event.target.value)}
                        placeholder={transferWorkspace?.name || ""}
                    />
                </div>
            </PopUpDialog>
        </>
    );
};

export default Workspaces;
